/** @format */

import {
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	SimpleChanges
} from '@angular/core';
import { backwardCompatibilityProperties, defaultProperties } from './pinch-to-zoom.properties';

import { IvyPinch, IvyPinchProperties } from './ivypinch';
import { Properties } from './pinch-to-zoom.interfaces';
import { isPlatformBrowser, CommonModule } from '@angular/common';

@Component({
	selector: 'pdp-pinch-to-zoom, [pdp-pinch-to-zoom]',
	exportAs: 'pdpPinchToZoom',
	templateUrl: './pinch-to-zoom.component.html',
	styleUrl: './pinch-to-zoom.component.scss',
	imports: [CommonModule]
})
export class PinchToZoomComponent implements OnDestroy, OnInit, OnChanges {
	pinchZoom: IvyPinch;
	_properties: Properties;

	@Input() transitionDuration = 200;
	@Input() autoZoomOut = false;
	@Input() limitZoom: number | 'original image size';
	@Input() disabled = false;
	@Input() disablePan: boolean;
	@Input() overflow: 'hidden' | 'visible';
	@Input() backgroundColor = 'rgb(255,255,255)';
	@Input() limitPan: boolean;
	@Input() minPanScale = 0.1;
	@Input() minScale = 0.1;
	@Input() autoHeight = false;

	@Output() events: EventEmitter<{ detail?: unknown; name: string }> = new EventEmitter();

	@HostBinding('style.overflow')
	get hostOverflow() {
		return this.properties['overflow'];
	}
	@HostBinding('style.background-color')
	get hostBackgroundColor() {
		return this.properties['backgroundColor'];
	}

	get isDisabled() {
		return this.properties['disabled'];
	}

	get scale() {
		return this.pinchZoom.scale;
	}

	get x() {
		return this.pinchZoom.moveX;
	}

	get y() {
		return this.pinchZoom.moveY;
	}

	get isZoomedIn() {
		return this.scale > 0.1;
	}

	get maxScale() {
		return this.pinchZoom.maxScale;
	}

	get isZoomLimitReached() {
		return this.scale >= this.maxScale;
	}

	set properties(value: Properties) {
		if (value) {
			this._properties = value;
		}
	}
	get properties() {
		return this._properties;
	}

	constructor(
		private readonly elementRef: ElementRef,
		@Inject(PLATFORM_ID) private readonly platformId: object
	) {
		this.applyOptionsDefault(defaultProperties, {});
	}

	ngOnInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.initPinchZoom();

			/* Calls the method until the image size is available */
			this.pollLimitZoom();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		let changedOptions = this.getProperties(changes);
		changedOptions = this.renameProperties(changedOptions);

		this.applyOptionsDefault(defaultProperties, changedOptions);
	}

	ngOnDestroy() {
		this.destroy();
	}

	initPinchZoom() {
		if (this.properties['disabled']) {
			return;
		}

		this.properties['element'] =
			this.elementRef.nativeElement.querySelector('.pinch-zoom-content');
		this.properties['eventHandler'] = this.events;
		this.pinchZoom = new IvyPinch(<IvyPinchProperties>this.properties);
	}

	getProperties(changes: SimpleChanges) {
		let properties: Properties = {};

		for (const prop in changes) {
			if (Object.prototype.hasOwnProperty.call(changes, prop)) {
				if (prop !== 'properties') {
					properties[prop] = changes[prop].currentValue;
				}
				if (prop === 'properties') {
					properties = changes[prop].currentValue;
				}
			}
		}
		return properties;
	}

	renameProperties(options: Properties) {
		for (const prop in options) {
			if (backwardCompatibilityProperties[prop]) {
				options[backwardCompatibilityProperties[prop]] = options[prop];
				delete options[prop];
			}
		}

		return options;
	}

	applyOptionsDefault(defaultOptions, options): void {
		this.properties = { ...defaultOptions, ...options };
	}

	pollLimitZoom() {
		this.pinchZoom.pollLimitZoom();
	}

	public setTransform(properties: {
		scale?: number;
		transitionDuration?: number;
		x?: number;
		y?: number;
	}) {
		this.pinchZoom.setTransform(properties);
	}

	public reset() {
		this.pinchZoom.resetScale();
	}

	public destroy() {
		if (!this.pinchZoom) return;

		this.pinchZoom.destroy();
	}
}
